import { Button } from '../Input2';
import { Alerts, General } from '../StrokeIcons';

import Popup, { type PopupProps } from './Popup';

type DeadContactDeletePopupProps = PopupProps & {
  /** How many contacts are being confirmed for deletion. Defaults to 1. */
  count?: number;
  onDelete: () => void;
  isDeleting?: boolean;
};

/**
 * Confirm-then-delete dialog for contacts whose LinkedIn profile Enhance found to be
 * dead & unrepairable (POS-2287). Enhance never deletes silently — it surfaces this
 * popup so the user is the final check against removing a still-alive contact (e.g. a
 * profile that only looks gone because of a stale vanity handle). Handles the single
 * (`count` 1) and bulk (`count` > 1) cases with one component so the dashboard detail
 * pane, the extension sidebar, and the multi-select popup all read the same copy.
 */
export default function DeadContactDeletePopup({
  onClose,
  onDelete,
  count = 1,
  isDeleting = false,
}: DeadContactDeletePopupProps) {
  const plural = count > 1;
  return (
    <Popup onClose={onClose} className="max-w-md p-0">
      <div className="flex flex-col items-center gap-2 px-5 pt-5 text-center">
        <div className="flex size-9 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
          <Alerts.AlertTriangle className="size-4 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {plural ? `Delete ${count} contacts?` : 'Delete this contact?'}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {plural
            ? `We couldn't enhance ${count} contacts — their LinkedIn profiles no longer exist and we couldn't find updated ones, so they can't be fixed. Delete them? This can't be undone.`
            : "We couldn't enhance this contact — its LinkedIn profile no longer exists and we couldn't find an updated one, so it can't be fixed. Delete it? This can't be undone."}
        </p>
      </div>

      <div className="flex justify-end gap-2 px-5 py-4">
        <Button size="sm" onClick={onClose} disabled={isDeleting}>
          Keep
        </Button>
        <Button size="sm" color="red" onClick={onDelete} disabled={isDeleting} icon={<General.Trash01 />}>
          {plural ? 'Delete them' : 'Delete'}
        </Button>
      </div>
    </Popup>
  );
}
